import { useState, useEffect } from 'react';
import apiClient from '../services/axiosInterceptor';

export interface TimeSlotOption {
  label: string;
  value: string;
  id: string;
  startTime: string;
  endTime: string;
  slotType: string;
  periodNumber: number;
}

interface TimeSlotResponse {
  id: number;
  timetable_type_id: number;
  period_number: number;
  period_label: string;
  start_time: string;
  end_time: string;
  slot_type: string;
}

interface TimeSlotsApiResponse {
  status: 'success' | 'error';
  data: TimeSlotResponse[];
  message?: string;
}

export const useTimeSlots = (timetableTypeId?: string | null) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlotOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTimeSlots = async () => {
      // Don't fetch if no timetableTypeId is provided
      if (!timetableTypeId) {
        setTimeSlots([]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get<TimeSlotsApiResponse>(
          `get-time-slots?timetable_type_id=${timetableTypeId}`
        );

        if (response.data.status === 'success') {
          if (response.data.data.length === 0) {
            setTimeSlots([]);
            setError('No time slots available for this timetable type');
          } else {
            const options: TimeSlotOption[] = response.data.data
              .sort((a, b) => a.period_number - b.period_number)
              .map((slot) => ({
                label: `${slot.period_label} (${slot.start_time} - ${slot.end_time})`,
                value: slot.id.toString(),
                id: slot.id.toString(),
                startTime: slot.start_time,
                endTime: slot.end_time,
                slotType: slot.slot_type,
                periodNumber: slot.period_number,
              }));

            setTimeSlots(options);
          }
        } else {
          setError(response.data.message || 'Failed to load time slots.');
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'An error occurred while fetching time slots.';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeSlots();
  }, [timetableTypeId]);

  return {
    timeSlots,
    loading,
    error,
  };
};